import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';

declare var bootstrap: any;

import { HeaderComponent } from '../../common/header/header.component';
import { SidebarComponent } from '../../common/slidebar/sidebar.components';
import { PaginatorComponent } from '../../common/paginator/paginator.component';
import { ContactDirectoryService } from '../../../services/contact-directory/contact-directory.service';
import { UserAccountService } from '../../../services/user-account/user-account.service';
import { ContactFormDialogComponent } from '../contact-form-dialog/contact-form-dialog.component';

@Component({
  selector: 'app-my-contributions',
  imports: [
    HeaderComponent,
    RouterLink,
    CommonModule,
    FormsModule,
    PaginatorComponent,
    SidebarComponent
  ],
  templateUrl: './my-contributions.component.html',
  styleUrl: './my-contributions.component.css',
})
export class MyContributionsComponent implements OnInit {
  contributions: any[] = [];
  updateRequests: any[] = [];
  isLoading: boolean = false;
  selectAll: boolean = false;
  activeListTab: 'contributions' | 'update_requests' = 'contributions';

  // Pagination properties
  currentPage: number = 1;
  itemsPerPage: number = 10;
  totalItems: number = 0;
  offset: number = 0;

  // Filter properties
  selectedFilterType: string = 'status';

  filterTypes: any[] = [
    { label: 'Status', value: 'status' },
    { label: 'Date Added', value: 'date-added' },
  ];

  statusFilter: string = '';
  startDateFilter: string = '';
  endDateFilter: string = '';

  // Available credits
  availableCredits: number = 0;

  // Summary stats
  yourRank: number = 0;
  yourCredits: number = 0;
  totalCreditsEarned: number = 0;
  totalContributions: number = 0;
  approvedSubmissions: number = 0;

  // Delete modal properties
  selectedContributionForDelete: any = null;
  selectedContributionsForDelete: any[] = [];
  deletionRequestReason: string = 'Submitted by mistake, need to correct data';
  isDeleting: boolean = false;
  isBulkDelete: boolean = false;
  deleteModal: any = null;

  /** While cancelling an update request (DELETE my-update-requests/:id). */
  cancellingUpdateRequestId: string | null = null;

  cancelUpdateRequestModal: any = null;
  /** Row selected in the table; confirmed in the cancel modal. */
  pendingCancelUpdateRequestRow: any = null;

  constructor(
    private contactDirectoryService: ContactDirectoryService,
    private userAccountService: UserAccountService,
    private dialog: MatDialog,
    private toastr: ToastrService,
  ) { }

  ngOnInit() {
    this.getAvailableCredits();
    this.loadContributions();
  }

  getAvailableCredits() {
    this.userAccountService.getUserProfile().subscribe({
      next: (response) => {
        const subscription = response?.data?.subscriptions?.[0] || {};
        this.availableCredits = this.userAccountService.pickAvailableCredits(response);
        this.yourCredits = this.availableCredits;
      },
      error: (error) => {
        console.error('Error loading available credits:', error);
        this.availableCredits = 0;
        this.yourCredits = 0;
      },
    });
  }

  loadContributions() {
    this.isLoading = true;
    this.offset = (this.currentPage - 1) * this.itemsPerPage;

    this.contactDirectoryService
      .getContributions(
        this.itemsPerPage,
        this.offset,
        this.statusFilter || undefined,
        this.startDateFilter || undefined,
        this.endDateFilter || undefined,
      )
      .subscribe({
        next: (response: any) => {
          if (response && response.data) {
            this.yourRank = response.data.user_rank ?? 0;
            this.totalCreditsEarned = response.data.total_credits_earned ?? 0;
            let mappedContributions = this.mapContributions(
              response.data.rows || [],
            );
            let mappedUpdateRequests = this.mapUpdateRequests(
              response.data.update_requests || [],
            );

            // Contributions: server applies status + date filters via getContributions params.
            mappedContributions =
              this.applyClientSideFilters(mappedContributions);

            // Update requests: same response often includes update_requests that are NOT filtered
            // by the contributions `status` / date query params — align them to the toolbar here.
            mappedUpdateRequests = this.applyToolbarFiltersToUpdateRequests(
              mappedUpdateRequests,
            );

            this.contributions = mappedContributions;
            this.updateRequests = mappedUpdateRequests;
            this.totalItems =
              this.activeListTab === 'contributions'
                ? (response.data.count || mappedContributions.length)
                : mappedUpdateRequests.length;

            // Calculate summary stats
            this.totalContributions = response.data.count || 0;
            this.calculateSummaryStats();
          } else {
            this.contributions = [];
            this.updateRequests = [];
            this.totalItems = 0;
            this.totalContributions = 0;
            this.yourRank = 0;
            this.totalCreditsEarned = 0;
            this.calculateSummaryStats();
          }
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading contributions:', error);
          this.contributions = [];
          this.updateRequests = [];
          this.totalItems = 0;
          this.totalContributions = 0;
          this.yourRank = 0;
          this.totalCreditsEarned = 0;
          this.isLoading = false;
        },
      });
  }

  mapContributions(rows: any[]): any[] {
    if (!rows || !Array.isArray(rows)) {
      return [];
    }

    return rows.map((item: any) => {
      const rowData = item.row_data || {};
      return {
        submissionId: item.submission_id, // Store submission_id for deletion
        organizationName: rowData.operator || 'N/A', // Operator name
        officerName: rowData.name || 'N/A', // Nodal officer name
        operatorType: rowData.operator_type || 'N/A', // Operator type
        dateSubmitted: this.formatDate(item.created_on),
        status: item.status || 'pending',
        creditsEarned: parseFloat(item.credits_awarded) || 0,
        selected: false,
        // Store full original data for editing
        originalData: item,
        rowData: rowData,
      };
    });
  }

  mapUpdateRequests(rows: any[]): any[] {
    if (!rows || !Array.isArray(rows)) {
      return [];
    }

    return rows.map((item: any) => {
      const updated = item.updated_row_data || {};
      return {
        updateRequestId: item.update_request_id || '',
        submissionId: item.submission_id || '',
        organizationName: updated.operator || 'N/A',
        officerName: updated.name || 'N/A',
        operatorType: updated.operator_type || 'N/A',
        dateSubmitted: this.formatDate(item.created_on),
        status: item.status || 'pending',
        creditsEarned: 0,
        requestReason: item.request_reason || 'N/A',
        rejectionReason: item.rejection_reason || null,
        selected: false,
        originalData: item,
      };
    });
  }

  calculateSummaryStats() {
    this.approvedSubmissions = this.contributions.filter(
      (c) => c.status?.toLowerCase() === 'approved',
    ).length;
  }

  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  }

  getStatusClass(status: string): string {
    const statusLower = status?.toLowerCase() || '';
    if (statusLower === 'approved') {
      return 'status-badge verified';
    } else if (statusLower === 'rejected') {
      return 'status-badge rejected';
    }
    return 'status-badge pending';
  }

  canCancelUpdateRequest(row: any): boolean {
    const s = (row?.status ?? '').toString().toLowerCase();
    return s === 'pending';
  }

  openCancelUpdateRequestModal(row: any) {
    const id = (row?.updateRequestId ?? '').toString().trim();
    if (!id) {
      this.toastr.error('Update request ID not found', 'Error');
      return;
    }
    if (!this.canCancelUpdateRequest(row)) {
      this.toastr.warning('Only pending update requests can be cancelled', 'Cannot cancel');
      return;
    }

    this.pendingCancelUpdateRequestRow = row;

    const modalElement = document.getElementById('cancelUpdateRequestModal');
    if (modalElement) {
      this.cancelUpdateRequestModal = new bootstrap.Modal(modalElement);
      this.cancelUpdateRequestModal.show();

      modalElement.addEventListener(
        'hidden.bs.modal',
        () => {
          if (this.cancellingUpdateRequestId !== id) {
            this.pendingCancelUpdateRequestRow = null;
          }
        },
        { once: true },
      );
    }
  }

  confirmCancelUpdateRequest() {
    const row = this.pendingCancelUpdateRequestRow;
    const id = (row?.updateRequestId ?? '').toString().trim();
    if (!id) {
      this.toastr.error('Update request ID not found', 'Error');
      return;
    }
    if (!this.canCancelUpdateRequest(row)) {
      this.toastr.warning('Only pending update requests can be cancelled', 'Cannot cancel');
      this.hideCancelUpdateRequestModal();
      return;
    }

    this.cancellingUpdateRequestId = id;
    this.contactDirectoryService.deleteMyUpdateRequest(id).subscribe({
      next: () => {
        this.cancellingUpdateRequestId = null;
        this.pendingCancelUpdateRequestRow = null;
        this.hideCancelUpdateRequestModal();
        this.toastr.success('Update request cancelled');
        this.loadContributions();
      },
      error: (error: any) => {
        this.cancellingUpdateRequestId = null;
        const errorMessage =
          error?.error?.message_key ||
          error?.error?.message ||
          error?.message ||
          'Failed to cancel update request.';
        this.toastr.error(errorMessage, 'Error');
      },
    });
  }

  private hideCancelUpdateRequestModal() {
    if (this.cancelUpdateRequestModal) {
      this.cancelUpdateRequestModal.hide();
    }
  }

  onFilterTypeChange() {
    this.reset();
  }

  reset() {
    this.currentPage = 1;
    this.statusFilter = '';
    this.startDateFilter = '';
    this.endDateFilter = '';
    this.loadContributions();
  }

  onStatusFilterChange(status: string) {
    this.statusFilter = status;
    this.currentPage = 1;
    this.loadContributions();
  }

  onStartDateFilterChange(startDate: string) {
    this.startDateFilter = startDate;
    this.currentPage = 1;

    if (this.startDateFilter === '' && this.endDateFilter === '') {
      this.loadContributions();
    }

    if (this.startDateFilter && this.endDateFilter) {
      this.loadContributions();
    }
  }

  onEndDateFilterChange(endDate: string) {
    this.endDateFilter = endDate;
    this.currentPage = 1;

    if (this.startDateFilter === '' && this.endDateFilter === '') {
      this.loadContributions();
    }

    if (this.startDateFilter && this.endDateFilter) {
      this.loadContributions();
    }
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.loadContributions();
    // Scroll to top of results
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  setActiveListTab(tab: 'contributions' | 'update_requests') {
    if (this.activeListTab === tab) {
      return;
    }
    this.activeListTab = tab;
    this.currentPage = 1;
    this.selectAll = false;
    this.loadContributions();
  }

  clearFilters() {
    this.reset();
  }

  get showingResultsText(): string {
    if (this.totalItems === 0) {
      return 'Showing 0 results';
    }
    const start = this.offset + 1;
    const end = Math.min(this.offset + this.itemsPerPage, this.totalItems);
    return `Showing ${start} to ${end} of ${this.totalItems} results`;
  }

  onAddContribution() {
    const dialogRef = this.dialog.open(ContactFormDialogComponent, {
      width: '520px',
      maxWidth: '95vw',
      disableClose: false,
      panelClass: 'contact-form-dialog',
      autoFocus: false,
      hasBackdrop: true,
      backdropClass: 'contact-form-dialog-backdrop',
      enterAnimationDuration: '300ms',
      exitAnimationDuration: '300ms',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.success) {
        // Reload contributions to show the latest entry
        this.currentPage = 1;
        this.loadContributions();
        // Refresh available credits
        this.getAvailableCredits();
      }
    });
  }

  applyClientSideFilters(contributions: any[]): any[] {
    // No client-side filtering needed for contributions (API handles toolbar filters).
    return contributions;
  }

  /**
   * Apply the same toolbar status / date selection to update-request rows.
   * Backend often filters only `rows` (contributions), not `update_requests`.
   */
  private applyToolbarFiltersToUpdateRequests(rows: any[]): any[] {
    if (!Array.isArray(rows) || rows.length === 0) {
      return [];
    }

    let list = [...rows];

    if (this.statusFilter) {
      const want = this.statusFilter.toLowerCase();
      list = list.filter(
        (r) => (r.status || '').toString().toLowerCase() === want,
      );
    }

    if (this.startDateFilter && this.endDateFilter) {
      const start = new Date(this.startDateFilter);
      const end = new Date(this.endDateFilter);
      const startDay = new Date(
        start.getFullYear(),
        start.getMonth(),
        start.getDate(),
      );
      const endDay = new Date(
        end.getFullYear(),
        end.getMonth(),
        end.getDate(),
        23,
        59,
        59,
        999,
      );

      list = list.filter((r) => {
        const raw =
          r.originalData?.created_on ||
          r.originalData?.updated_on ||
          null;
        if (!raw) {
          return false;
        }
        const dt = new Date(raw);
        if (Number.isNaN(dt.getTime())) {
          return false;
        }
        return dt >= startDay && dt <= endDay;
      });
    }

    return list;
  }

  toggleSelectAll(event: Event) {
    const target = event.target as HTMLInputElement;
    this.selectAll = target.checked;
    this.contributions.forEach((contribution) => {
      contribution.selected = this.selectAll;
    });
  }

  toggleContributionSelection(contribution: any) {
    contribution.selected = !contribution.selected;
    // Update select all checkbox based on current selections
    this.selectAll = this.contributions.every((c) => c.selected);
  }

  getSelectedContributions(): any[] {
    if (this.activeListTab !== 'contributions') {
      return [];
    }
    return this.contributions.filter((c) => c.selected);
  }

  getSelectedContributionsForDelete(): any[] {
    return this.isBulkDelete ? this.selectedContributionsForDelete : [];
  }

  onEditContribution(contribution: any) {
    if (!contribution.originalData) {
      this.toastr.error('Contribution data not available for editing', 'Error');
      return;
    }

    // Open the same dialog with pre-populated data
    const dialogRef = this.dialog.open(ContactFormDialogComponent, {
      width: '520px',
      maxWidth: '95vw',
      disableClose: false,
      panelClass: 'contact-form-dialog',
      autoFocus: false,
      hasBackdrop: true,
      backdropClass: 'contact-form-dialog-backdrop',
      enterAnimationDuration: '300ms',
      exitAnimationDuration: '300ms',
      data: {
        isEditMode: true,
        contribution: contribution.originalData,
        submissionId: contribution.submissionId,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.success) {
        // Reload contributions to show the updated entry
        this.currentPage = 1;
        this.loadContributions();
        // Refresh available credits
        this.getAvailableCredits();
      }
    });
  }

  onDeleteContribution(contribution: any) {
    if (!contribution.submissionId) {
      this.toastr.error('Submission ID not found', 'Error');
      return;
    }

    this.isBulkDelete = false;
    this.selectedContributionForDelete = contribution;
    this.selectedContributionsForDelete = [];
    this.deletionRequestReason = 'Submitted by mistake, need to correct data';
    this.isDeleting = false;

    // Show Bootstrap modal
    const modalElement = document.getElementById('deleteContributionModal');
    if (modalElement) {
      this.deleteModal = new bootstrap.Modal(modalElement);
      this.deleteModal.show();
      
      // Reset modal state when closed without submitting
      modalElement.addEventListener('hidden.bs.modal', () => {
        if (!this.isDeleting) {
          this.selectedContributionForDelete = null;
          this.selectedContributionsForDelete = [];
          this.deletionRequestReason = 'Submitted by mistake, need to correct data';
          this.isBulkDelete = false;
        }
      }, { once: true });
    }
  }

  onBulkDelete() {
    const selected = this.getSelectedContributions();
    
    if (selected.length === 0) {
      this.toastr.warning('Please select at least one contribution to delete', 'No Selection');
      return;
    }

    // Validate all selected items have submission IDs
    const invalidItems = selected.filter((c) => !c.submissionId);
    if (invalidItems.length > 0) {
      this.toastr.error('Some selected items are missing submission IDs', 'Error');
      return;
    }

    this.isBulkDelete = true;
    this.selectedContributionForDelete = null;
    this.selectedContributionsForDelete = selected;
    this.deletionRequestReason = 'Submitted by mistake, need to correct data';
    this.isDeleting = false;

    // Show Bootstrap modal
    const modalElement = document.getElementById('deleteContributionModal');
    if (modalElement) {
      this.deleteModal = new bootstrap.Modal(modalElement);
      this.deleteModal.show();
      
      // Reset modal state when closed without submitting
      modalElement.addEventListener('hidden.bs.modal', () => {
        if (!this.isDeleting) {
          this.selectedContributionForDelete = null;
          this.selectedContributionsForDelete = [];
          this.deletionRequestReason = 'Submitted by mistake, need to correct data';
          this.isBulkDelete = false;
        }
      }, { once: true });
    }
  }

  confirmDelete() {
    if (this.isBulkDelete) {
      // Bulk delete
      if (this.selectedContributionsForDelete.length === 0) {
        this.toastr.error('No contributions selected', 'Error');
        return;
      }

      const invalidItems = this.selectedContributionsForDelete.filter((c) => !c.submissionId);
      if (invalidItems.length > 0) {
        this.toastr.error('Some selected items are missing submission IDs', 'Error');
        return;
      }
    } else {
      // Single delete
      if (!this.selectedContributionForDelete || !this.selectedContributionForDelete.submissionId) {
        this.toastr.error('Submission ID not found', 'Error');
        return;
      }
    }

    if (!this.deletionRequestReason || this.deletionRequestReason.trim() === '') {
      this.toastr.error('Please provide a reason for deletion', 'Error');
      return;
    }

    this.isDeleting = true;

    // Prepare payload
    const submissionIds = this.isBulkDelete
      ? this.selectedContributionsForDelete.map((c) => c.submissionId)
      : [this.selectedContributionForDelete.submissionId];

    const payload = {
      submission_ids: submissionIds,
      request_reason: this.deletionRequestReason.trim(),
    };

    this.contactDirectoryService.requestDeletion(payload).subscribe({
      next: (response: any) => {
        console.log('Deletion request successful:', response);
        this.toastr.success(
          `Deletion request submitted successfully for ${submissionIds.length} contribution(s).`,
          'Success'
        );
        
        // Close modal
        if (this.deleteModal) {
          this.deleteModal.hide();
        }
        
        // Reset form
        this.selectedContributionForDelete = null;
        this.selectedContributionsForDelete = [];
        this.deletionRequestReason = 'Submitted by mistake, need to correct data';
        this.isDeleting = false;
        this.isBulkDelete = false;
        
        // Clear selections
        this.contributions.forEach((c) => (c.selected = false));
        this.selectAll = false;
        
        // Reload contributions to reflect the updated status
        this.loadContributions();
      },
      error: (error: any) => {
        console.error('Error requesting deletion:', error);
        const errorMessage =
          error?.error?.message_key ||
          error?.error?.message ||
          error?.message ||
          'Failed to submit deletion request. Please try again.';
        this.toastr.error(errorMessage, 'Error');
        this.isDeleting = false;
      },
    });
  }
}
